/**
 *
 * CommunicationHistory
 *
 */
import React, { memo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import type { PopconfirmProps } from 'antd';
import { Button, Col, Popconfirm, Row, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import moment from 'moment';

import { useQuery } from '@apollo/client';
import {
  GET_COMMUNICATION_HISTORY_LIST,
  GET_INITIATIVE_USHER_DETAILS,
} from 'app/graphql/queries';

import { Loading } from 'app/components/Loading';

import { DeleteCommunicationHistoryRecord } from 'app/redux/actions/InitiativeUsher';

import EditCommunicationRecordModal from './EditCommunicationRecordModal';
import NewCommunicationRecordModal from './NewCommunicationRecordModal';

const { Title } = Typography;

interface Props {}

export const CommunicationHistory = memo((props: Props) => {
  const dispatch = useDispatch();

  const { initiativeUsherID } = useParams();

  const {
    loading: communicationHistoryLoading,
    data: communicationHistoryData,
    refetch: communicationHistoryRefetch,
  } = useQuery(GET_COMMUNICATION_HISTORY_LIST, {
    variables: { initiativeUsherId: initiativeUsherID },
  });

  const { loading: registrationDetailsLoading, data: registrationDetailsData } =
    useQuery(GET_INITIATIVE_USHER_DETAILS, {
      variables: { initiativeUsherId: initiativeUsherID },
    });

  const [data, setData] = useState(null);

  const [newRecordModal, setNewRecordModal] = useState(false);
  const [editRecordModal, setEditRecordModal] = useState(false);

  const confirm = async (recordID: number) => {
    await dispatch(DeleteCommunicationHistoryRecord(recordID));
    communicationHistoryRefetch();
  };

  const cancel: PopconfirmProps['onCancel'] = () => {};

  const columns: ColumnsType<any> = [
    {
      title: 'Date',
      align: 'center',
      render: (_, record) => moment(record.node.date).format('DD/MM/YYYY'),
    },
    {
      title: 'Time',
      align: 'center',
      render: (_, record) =>
        record.node.time
          ? moment(record.node.time, 'HH:mm:ss').format('h:mm A')
          : 'N/A',
    },
    {
      title: 'Communication Method',
      align: 'center',
      render: (_, record) => record.node.communicationMethod,
    },
    {
      title: 'Comments',
      align: 'center',
      render: (_, record) => record.node.comments || 'N/A',
    },
    {
      title: 'Actions',
      align: 'center',
      render: (_: any, record: any) => {
        return (
          <Space size="middle">
            <Button
              type="text"
              onClick={() => {
                setData(record.node);
                setEditRecordModal(true);
              }}
            >
              <EditOutlined style={{ fontSize: '150%' }} />
            </Button>

            <Popconfirm
              title="Delete Record"
              description="Are you sure to delete this record?"
              onConfirm={() => confirm(record.node.pk)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
              icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
            >
              <Button type="text">
                <DeleteOutlined style={{ fontSize: '150%' }} />
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  if (communicationHistoryLoading || registrationDetailsLoading)
    return <Loading />;

  const registrationData =
    registrationDetailsData?.initiativeUshers?.edges[0]?.node;

  return (
    <>
      <Row gutter={[8, 8]} className="mb-4">
        <Col>
          <Title level={3} className="mb-0">
            {registrationData.usher.englishFullName +
              ' - ' +
              registrationData.initiative.englishName +
              ' | Communication History'}

            <PlusCircleOutlined
              className="mx-3"
              onClick={() => setNewRecordModal(true)}
            />
          </Title>
        </Col>
      </Row>

      <Table
        dataSource={communicationHistoryData.communicationHistory.edges}
        columns={columns}
        pagination={false}
        rowKey={record => record.node.pk}
      />

      <NewCommunicationRecordModal
        show={newRecordModal}
        setShow={setNewRecordModal}
        initiativeUsherID={initiativeUsherID}
      />

      {data && (
        <EditCommunicationRecordModal
          show={editRecordModal}
          setShow={setEditRecordModal}
          data={data}
          setData={setData}
        />
      )}
    </>
  );
});
